import React, { useEffect, useState } from "react";
import { Sparkles, Send, Save, Trash2, Eye, EyeOff, X } from "lucide-react";
import { blogApi } from "../../api";
import { toast } from "sonner";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return ""; } };

export default function BlogView() {
  const [posts, setPosts] = useState([]);
  const [sel, setSel] = useState(null);
  const [f, setF] = useState({ title: "", excerpt: "", body_html: "", tag: "Market Update" });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = async () => { try { setPosts(await blogApi.adminList()); } catch { toast.error("Failed to load posts"); } };
  useEffect(() => { load(); }, []);

  const edit = (p) => { setSel(p.id); setF({ title: p.title, excerpt: p.excerpt, body_html: p.body_html, tag: p.tag || "Market Update" }); };

  const generate = async () => {
    setBusy(true);
    try {
      const p = await blogApi.generate();
      toast.success("AI draft generated — review & publish");
      await load(); edit(p);
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!sel) return;
    if (!f.title || !f.body_html) { toast.error("Title and body are required"); return; }
    try { await blogApi.update(sel, f); toast.success("Saved"); await load(); } catch { toast.error("Could not save"); }
  };

  const publish = async () => { await blogApi.publish(sel); toast.success("Published — now live on the site & popup"); load(); };
  const unpublish = async () => { await blogApi.unpublish(sel); toast("Moved back to draft"); load(); };
  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await blogApi.remove(id); toast("Deleted");
    if (sel === id) { setSel(null); setF({ title: "", excerpt: "", body_html: "", tag: "Market Update" }); }
    load();
  };

  const current = posts.find((p) => p.id === sel);

  return (
    <div data-testid="blog-view" style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: "1.4rem", alignItems: "start" }}>
      <div className="card" style={{ padding: "1.2rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <span className="eyebrow" style={{ color: "var(--muted-2)" }}>Weekly Updates</span>
        </div>
        <button className="btn-gold" data-testid="generate-blog-btn" style={{ width: "100%", marginBottom: "1rem" }} disabled={busy} onClick={generate}>
          <Sparkles size={14} /> {busy ? "Generating…" : "Generate AI Draft"}
        </button>
        <div style={{ display: "grid", gap: "0.5rem" }}>
          {posts.map((p) => (
            <div key={p.id} data-testid={`blog-item-${p.id}`} onClick={() => edit(p)} style={{ padding: "0.7rem", borderRadius: 6, cursor: "pointer", border: `1px solid ${sel === p.id ? "var(--gold)" : "rgba(255,255,255,0.08)"}`, background: sel === p.id ? "var(--gold-soft)" : "transparent" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: "0.8rem", color: sel === p.id ? "var(--gold)" : "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</span>
                <button onClick={(e) => { e.stopPropagation(); remove(p.id); }} style={{ background: "none", border: "none", color: "var(--red)", cursor: "pointer", flexShrink: 0 }}><Trash2 size={13} /></button>
              </div>
              <span style={{ fontSize: "0.62rem", padding: "0.1rem 0.5rem", borderRadius: 999, background: p.status === "published" ? "rgba(123,196,127,0.15)" : "rgba(255,255,255,0.06)", color: p.status === "published" ? "var(--green)" : "var(--muted-2)" }}>{p.status}</span>
            </div>
          ))}
          {posts.length === 0 && <p className="body-text" style={{ fontSize: "0.76rem" }}>No posts yet. Generate your first weekly draft.</p>}
        </div>
      </div>

      <div className="card" style={{ padding: "1.6rem" }}>
        {!sel ? (
          <p className="body-text" style={{ fontSize: "0.88rem" }}>Select a post to edit, or generate a new AI draft. Published posts appear on the site's Insights section and as the weekly popup for visitors.</p>
        ) : (
          <div style={{ display: "grid", gap: "0.9rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span className="eyebrow" style={{ color: "var(--muted-2)" }}>Editing · {current?.status} {current?.published_at ? `· ${fmtDate(current.published_at)}` : ""}</span>
            </div>
            <input className="field" data-testid="blog-title" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
            <input className="field" data-testid="blog-tag" placeholder="Tag" value={f.tag} onChange={(e) => setF({ ...f, tag: e.target.value })} />
            <input className="field" data-testid="blog-excerpt" placeholder="Excerpt (one sentence)" value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} />
            <textarea className="field" data-testid="blog-body" placeholder="Body HTML" value={f.body_html} onChange={(e) => setF({ ...f, body_html: e.target.value })} rows={12} style={{ fontFamily: "monospace", fontSize: "0.78rem", resize: "vertical" }} />
            <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
              <button className="btn-gold" data-testid="save-blog-btn" onClick={save}><Save size={14} /> Save</button>
              <button className="btn-dark" data-testid="preview-blog-btn" onClick={() => setPreview(true)}><Eye size={14} /> Preview</button>
              <div style={{ flex: 1 }} />
              {current?.status === "published"
                ? <button className="btn-dark" data-testid="unpublish-blog-btn" onClick={unpublish}><EyeOff size={14} /> Unpublish</button>
                : <button className="btn-gold" data-testid="publish-blog-btn" onClick={publish}><Send size={14} /> Publish</button>}
            </div>
          </div>
        )}
      </div>

      {preview && (
        <div onClick={() => setPreview(false)} data-testid="blog-preview-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
          <div onClick={(e) => e.stopPropagation()} className="card" style={{ background: "#0f0e13", width: "100%", maxWidth: 620, maxHeight: "85vh", overflow: "auto", padding: "2.4rem", position: "relative" }}>
            <button onClick={() => setPreview(false)} style={{ position: "absolute", top: 14, right: 14, background: "none", border: "none", color: "var(--muted)", cursor: "pointer" }}><X size={18} /></button>
            <h2 className="font-serif" style={{ fontSize: "1.7rem", marginBottom: "1rem", color: "var(--text)" }}>{f.title}</h2>
            <div style={{ color: "var(--muted)", fontSize: "0.92rem", lineHeight: 1.8 }} dangerouslySetInnerHTML={{ __html: f.body_html }} />
          </div>
        </div>
      )}
    </div>
  );
}
